import React, { useEffect, useMemo } from 'react';
import { Checkbox, Col, Divider, Form, Input, Row } from 'antd';
import type { Rule } from 'antd/es/form';
import dayjs from 'dayjs';

import {
  CustomDatePicker,
  CustomInput,
  CustomSelect,
  CustomTimePicker,
  CustomTitle,
} from '../../../components';
import type {
  SpecialtyFieldSection,
  SpecialtyOptionField,
  SpecialtyOptionsResponse,
} from '../../../apis/specialtyBooking';
import { specialtyFieldChoices } from '../specialtyBookingHelpers';
import styles from '../BookShipmentDrawer.module.css';

const { TextArea } = Input;

const DIMENSION_FIELDS = [
  { key: 'length', label: 'Length', placeholder: 'L' },
  { key: 'width', label: 'Width', placeholder: 'W' },
  { key: 'height', label: 'Height', placeholder: 'H' },
] as const;

interface SpecialtyBookingFieldsProps {
  provider?: string | null;
  options: SpecialtyOptionsResponse | null;
  optionsLoading: boolean;
  requiresPieces: boolean;
  dateFormat: string;
  /** Optional Zoomlogi teams/products block (not sent to the carrier). */
  zoomlogiFields?: React.ReactNode;
}

function combineDateTime(
  date: unknown,
  time: unknown,
  fallbackHour = 9
): dayjs.Dayjs | null {
  if (!dayjs.isDayjs(date)) return null;
  const hour = dayjs.isDayjs(time) ? time.hour() : fallbackHour;
  const minute = dayjs.isDayjs(time) ? time.minute() : 0;
  return date.hour(hour).minute(minute).second(0).millisecond(0);
}

function PartyAddressFields({
  prefix,
}: {
  prefix: 'specialty_shipper' | 'specialty_recipient';
}) {
  return (
    <>
      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          Contact name <span className={styles.required}>*</span>
        </label>
        <Form.Item
          name={[prefix, 'name']}
          rules={[{ required: true, message: 'Enter contact name' }]}
          noStyle
        >
          <CustomInput placeholder='Name' width='100%' height='36px' />
        </Form.Item>
      </div>
      <div className={styles.fieldWrap}>
        <label className={styles.label}>Company</label>
        <Form.Item name={[prefix, 'company']} noStyle>
          <CustomInput placeholder='Company' width='100%' height='36px' />
        </Form.Item>
      </div>
      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          Address <span className={styles.required}>*</span>
        </label>
        <Form.Item
          name={[prefix, 'street']}
          rules={[{ required: true, message: 'Enter street address' }]}
          noStyle
        >
          <CustomInput placeholder='Street' width='100%' height='36px' />
        </Form.Item>
      </div>
      <div className={styles.fieldWrap}>
        <label className={styles.label}>Address line 2</label>
        <Form.Item name={[prefix, 'street2']} noStyle>
          <CustomInput
            placeholder='Suite / unit / room'
            width='100%'
            height='36px'
          />
        </Form.Item>
      </div>
      <Row gutter={12}>
        <Col span={12}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>
              City <span className={styles.required}>*</span>
            </label>
            <Form.Item
              name={[prefix, 'city']}
              rules={[{ required: true, message: 'Enter city' }]}
              noStyle
            >
              <CustomInput placeholder='City' width='100%' height='36px' />
            </Form.Item>
          </div>
        </Col>
        <Col span={12}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>State</label>
            <Form.Item name={[prefix, 'state']} noStyle>
              <CustomInput placeholder='State' width='100%' height='36px' />
            </Form.Item>
          </div>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>
              Postal code <span className={styles.required}>*</span>
            </label>
            <Form.Item
              name={[prefix, 'postalCode']}
              rules={[{ required: true, message: 'Enter postal code' }]}
              noStyle
            >
              <CustomInput
                placeholder='Postal code'
                width='100%'
                height='36px'
              />
            </Form.Item>
          </div>
        </Col>
        <Col span={12}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>
              Country <span className={styles.required}>*</span>
            </label>
            <Form.Item
              name={[prefix, 'country']}
              rules={[{ required: true, message: 'Enter country' }]}
              noStyle
            >
              <CustomInput placeholder='US' width='100%' height='36px' />
            </Form.Item>
          </div>
        </Col>
      </Row>
      <div className={styles.fieldWrap}>
        <label className={styles.label}>Location notes</label>
        <Form.Item name={[prefix, 'notes']} noStyle>
          <CustomInput
            placeholder='Gate code, dock, wing…'
            width='100%'
            height='36px'
          />
        </Form.Item>
      </div>
      <Row gutter={12}>
        <Col span={12}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>
              Phone <span className={styles.required}>*</span>
            </label>
            <Form.Item
              name={[prefix, 'phone']}
              rules={[{ required: true, message: 'Enter phone' }]}
              noStyle
            >
              <CustomInput placeholder='Phone' width='100%' height='36px' />
            </Form.Item>
          </div>
        </Col>
        <Col span={12}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>Email</label>
            <Form.Item name={[prefix, 'email']} noStyle>
              <CustomInput placeholder='Email' width='100%' height='36px' />
            </Form.Item>
          </div>
        </Col>
      </Row>
    </>
  );
}

function ProviderFieldControl({
  field,
  options,
}: {
  field: SpecialtyOptionField;
  options: SpecialtyOptionsResponse | null;
}) {
  const form = Form.useFormInstance();
  const namePath = ['specialty_provider_fields', field.key] as const;
  const parentWatchPath = [
    'specialty_provider_fields',
    field.optionsDependOn ?? '__unused_parent__',
  ] as const;
  const parentValue = Form.useWatch(parentWatchPath, form);
  const currentValue = Form.useWatch(namePath, form);
  const choices = specialtyFieldChoices(
    options,
    field,
    field.optionsDependOn ? parentValue : undefined
  );

  useEffect(() => {
    if (!field.optionsDependOn) return;
    const path = ['specialty_provider_fields', field.key] as const;
    if (currentValue == null || currentValue === '') {
      if (choices.length === 1) {
        form.setFieldValue(path, choices[0].value);
      }
      return;
    }
    const stillValid = choices.some(
      (c) => String(c.value) === String(currentValue)
    );
    if (!stillValid) {
      form.setFieldValue(
        path,
        choices.length === 1 ? choices[0].value : undefined
      );
    }
  }, [choices, currentValue, field.key, field.optionsDependOn, form]);

  if (field.type === 'boolean' || field.type === 'checkbox') {
    const booleanRules: Rule[] | undefined = field.required
      ? [
          {
            validator: async (_, value) => {
              if (value === true) return;
              throw new Error(`Confirm ${field.label}`);
            },
          },
        ]
      : undefined;
    return (
      <div className={styles.fieldWrap}>
        <Form.Item
          name={[...namePath]}
          valuePropName='checked'
          initialValue={false}
          rules={booleanRules}
          noStyle
        >
          <Checkbox>
            {field.label}
            {field.required ? (
              <span className={styles.required}> *</span>
            ) : null}
          </Checkbox>
        </Form.Item>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          {field.label}
          {field.required ? <span className={styles.required}> *</span> : null}
        </label>
        <Form.Item
          name={[...namePath]}
          initialValue={
            field.default != null ? String(field.default) : undefined
          }
          rules={
            field.required
              ? [{ required: true, message: `Enter ${field.label}` }]
              : undefined
          }
          noStyle
        >
          <TextArea
            rows={3}
            placeholder={
              field.placeholder != null
                ? String(field.placeholder)
                : field.label
            }
            className={styles.specialtyTextArea}
          />
        </Form.Item>
      </div>
    );
  }

  if (field.type === 'select' || choices.length > 0 || field.optionsDependOn) {
    const needsParent =
      Boolean(field.optionsDependOn) &&
      (parentValue == null || parentValue === '');
    return (
      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          {field.label}
          {field.required ? <span className={styles.required}> *</span> : null}
        </label>
        <Form.Item
          name={[...namePath]}
          dependencies={
            field.optionsDependOn
              ? [['specialty_provider_fields', field.optionsDependOn]]
              : undefined
          }
          rules={
            field.required
              ? [{ required: true, message: `Select ${field.label}` }]
              : undefined
          }
          noStyle
        >
          <CustomSelect
            placeholder={
              needsParent
                ? `Select ${
                    field.optionsDependOn === 'packageTypeId'
                      ? 'Packaging'
                      : field.optionsDependOn === 'account'
                        ? 'Account'
                        : field.optionsDependOn
                  } first`
                : choices.length
                  ? 'Select'
                  : 'No options for this account'
            }
            width='100%'
            height='36px'
            disabled={needsParent || choices.length === 0}
            options={choices.map((c) => ({
              label: c.label,
              value: c.value,
            }))}
          />
        </Form.Item>
      </div>
    );
  }

  return (
    <div className={styles.fieldWrap}>
      <label className={styles.label}>
        {field.label}
        {field.required ? <span className={styles.required}> *</span> : null}
      </label>
      <Form.Item
        name={[...namePath]}
        rules={
          field.required
            ? [{ required: true, message: `Enter ${field.label}` }]
            : undefined
        }
        noStyle
      >
        <CustomInput placeholder={field.label} width='100%' height='36px' />
      </Form.Item>
    </div>
  );
}

function ProviderSectionFields({
  options,
  optionsLoading,
  section,
  title,
}: {
  options: SpecialtyOptionsResponse | null;
  optionsLoading: boolean;
  section: SpecialtyFieldSection;
  title?: string;
}) {
  const fields: SpecialtyOptionField[] = useMemo(() => {
    const list = options?.fields;
    if (!Array.isArray(list)) return [];
    return list.filter((field) => (field.section || 'provider') === section);
  }, [options, section]);

  if (optionsLoading && section === 'provider') {
    return (
      <div className={styles.fieldWrap}>
        <label className={styles.label}>Loading provider options…</label>
      </div>
    );
  }

  if (!fields.length) return null;

  return (
    <>
      {title ? (
        <CustomTitle
          text={title}
          fontSize='18px'
          fontWeight='700'
          lineHeight='24px'
          marginBottom={16}
        />
      ) : null}
      {fields.map((field) => (
        <ProviderFieldControl key={field.key} field={field} options={options} />
      ))}
    </>
  );
}

const SpecialtyBookingFields: React.FC<SpecialtyBookingFieldsProps> = ({
  provider,
  options,
  optionsLoading,
  requiresPieces,
  dateFormat,
  zoomlogiFields,
}) => {
  const form = Form.useFormInstance();
  const notifyEmailRequired = provider === 'mercury';
  const dimensionsRequired = provider === 'mercury';
  const contentsRequired = requiresPieces || provider === 'lifecouriers';
  const showPackageQuantity = provider === 'mercury' || provider === 'biocair';
  const showReadyUntil = provider === 'mercury' || provider === 'biocair';
  const showDeliveryReady = provider === 'biocair' || provider === 'momentdash';
  const showDeliveryUntil = provider === 'biocair';
  const readyUntilHint =
    provider === 'biocair'
      ? 'Optional. If blank, pickup window ends 2 hours after ready time.'
      : provider === 'mercury'
        ? 'Optional. If blank, pickup window ends 4 hours after ready time.'
        : null;
  const pastReadyHint =
    provider === 'momentdash'
      ? 'If ready time is already past, pickup is moved to about 1 hour from now. Delivery ready must still be on or after that time.'
      : null;

  const readyUntilRules: Rule[] = [
    {
      validator: async (_, value) => {
        if (!dayjs.isDayjs(value)) return;
        const ready = combineDateTime(
          form.getFieldValue('specialty_ready_date'),
          form.getFieldValue('specialty_ready_time')
        );
        const until = combineDateTime(
          form.getFieldValue('specialty_ready_date'),
          value
        );
        if (ready && until && !until.isAfter(ready)) {
          throw new Error('Ready until must be after ready time');
        }
      },
    },
  ];

  const deliveryReadyRules: Rule[] = [
    {
      validator: async () => {
        const deliveryTime = form.getFieldValue(
          'specialty_delivery_ready_time'
        );
        const deliveryDate = form.getFieldValue(
          'specialty_delivery_ready_date'
        );
        if (!dayjs.isDayjs(deliveryTime) && !dayjs.isDayjs(deliveryDate)) {
          return;
        }
        const ready = combineDateTime(
          form.getFieldValue('specialty_ready_date'),
          form.getFieldValue('specialty_ready_time')
        );
        const delivery = combineDateTime(
          deliveryDate ?? form.getFieldValue('specialty_ready_date'),
          deliveryTime
        );
        let effectiveReady = ready;
        // MomentDash bumps past readyAt to ~now+1h before send.
        if (provider === 'momentdash' && ready && !ready.isAfter(dayjs())) {
          effectiveReady = dayjs().add(1, 'hour');
        }
        if (effectiveReady && delivery && delivery.isBefore(effectiveReady)) {
          throw new Error(
            provider === 'momentdash' && effectiveReady !== ready
              ? 'Delivery ready must be on or after the adjusted pickup time (about 1 hour from now)'
              : 'Delivery ready must be on or after pickup ready'
          );
        }
      },
    },
  ];

  const deliveryUntilRules: Rule[] = [
    {
      validator: async (_, value) => {
        if (!dayjs.isDayjs(value)) return;
        const hasDeliveryReady =
          dayjs.isDayjs(form.getFieldValue('specialty_delivery_ready_date')) ||
          dayjs.isDayjs(form.getFieldValue('specialty_delivery_ready_time'));
        if (!hasDeliveryReady) {
          throw new Error('Set delivery ready before delivery until');
        }
        const deliveryDate =
          form.getFieldValue('specialty_delivery_ready_date') ??
          form.getFieldValue('specialty_ready_date');
        const deliveryStart = combineDateTime(
          deliveryDate,
          form.getFieldValue('specialty_delivery_ready_time')
        );
        const deliveryUntil = combineDateTime(deliveryDate, value);
        if (
          deliveryStart &&
          deliveryUntil &&
          !deliveryUntil.isAfter(deliveryStart)
        ) {
          throw new Error('Delivery until must be after delivery ready time');
        }
      },
    },
  ];

  return (
    <>
      <CustomTitle
        text='Pickup'
        fontSize='18px'
        fontWeight='700'
        lineHeight='24px'
        marginBottom={16}
      />
      <PartyAddressFields prefix='specialty_shipper' />
      <Row gutter={12}>
        <Col span={showReadyUntil ? 10 : 14}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>
              Ready date <span className={styles.required}>*</span>
            </label>
            <Form.Item
              name='specialty_ready_date'
              rules={[{ required: true, message: 'Select ready date' }]}
              noStyle
            >
              <CustomDatePicker dateFormat={dateFormat} />
            </Form.Item>
          </div>
        </Col>
        <Col span={showReadyUntil ? 7 : 10}>
          <div className={styles.fieldWrap}>
            <label className={styles.label}>Ready time</label>
            <Form.Item name='specialty_ready_time' noStyle>
              <CustomTimePicker minuteStep={15} />
            </Form.Item>
          </div>
        </Col>
        {showReadyUntil ? (
          <Col span={7}>
            <div className={styles.fieldWrap}>
              <label className={styles.label}>Ready until</label>
              <Form.Item
                name='specialty_ready_until_time'
                dependencies={['specialty_ready_date', 'specialty_ready_time']}
                rules={readyUntilRules}
                noStyle
              >
                <CustomTimePicker minuteStep={15} />
              </Form.Item>
            </div>
          </Col>
        ) : null}
      </Row>
      {readyUntilHint ? (
        <div className={styles.specialtyFieldHint}>{readyUntilHint}</div>
      ) : null}
      {pastReadyHint ? (
        <div className={styles.specialtyFieldHint}>{pastReadyHint}</div>
      ) : null}
      <div className={styles.fieldWrap}>
        <label className={styles.label}>Pickup instructions</label>
        <Form.Item name='specialty_special_instructions' noStyle>
          <TextArea
            rows={3}
            placeholder='Dock hours, who to ask for, access notes…'
            className={styles.specialtyTextArea}
          />
        </Form.Item>
      </div>
      <ProviderSectionFields
        options={options}
        optionsLoading={optionsLoading}
        section='pickup'
      />

      <Divider style={{ margin: '16px 0' }} />

      <CustomTitle
        text='Delivery'
        fontSize='18px'
        fontWeight='700'
        lineHeight='24px'
        marginBottom={16}
      />
      <PartyAddressFields prefix='specialty_recipient' />
      {showDeliveryReady ? (
        <>
          <Row gutter={12}>
            <Col span={showDeliveryUntil ? 10 : 14}>
              <div className={styles.fieldWrap}>
                <label className={styles.label}>Delivery ready date</label>
                <Form.Item
                  name='specialty_delivery_ready_date'
                  dependencies={[
                    'specialty_ready_date',
                    'specialty_ready_time',
                    'specialty_delivery_ready_time',
                  ]}
                  rules={deliveryReadyRules}
                  noStyle
                >
                  <CustomDatePicker dateFormat={dateFormat} />
                </Form.Item>
              </div>
            </Col>
            <Col span={showDeliveryUntil ? 7 : 10}>
              <div className={styles.fieldWrap}>
                <label className={styles.label}>Delivery ready time</label>
                <Form.Item
                  name='specialty_delivery_ready_time'
                  dependencies={[
                    'specialty_ready_date',
                    'specialty_ready_time',
                    'specialty_delivery_ready_date',
                  ]}
                  rules={deliveryReadyRules}
                  noStyle
                >
                  <CustomTimePicker minuteStep={15} />
                </Form.Item>
              </div>
            </Col>
            {showDeliveryUntil ? (
              <Col span={7}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Delivery until</label>
                  <Form.Item
                    name='specialty_delivery_until_time'
                    dependencies={[
                      'specialty_delivery_ready_date',
                      'specialty_delivery_ready_time',
                      'specialty_ready_date',
                    ]}
                    rules={deliveryUntilRules}
                    noStyle
                  >
                    <CustomTimePicker minuteStep={15} />
                  </Form.Item>
                </div>
              </Col>
            ) : null}
          </Row>
          {showDeliveryUntil ? (
            <div className={styles.specialtyFieldHint}>
              Optional. Requires delivery ready. If blank, delivery window ends
              4 hours after delivery ready (or 4 hours after pickup ready + 1
              day when delivery ready is also blank).
            </div>
          ) : null}
        </>
      ) : null}
      <div className={styles.fieldWrap}>
        <label className={styles.label}>Delivery instructions</label>
        <Form.Item name='specialty_delivery_instructions' noStyle>
          <TextArea
            rows={3}
            placeholder='Receiving hours, fridge location, contact on site…'
            className={styles.specialtyTextArea}
          />
        </Form.Item>
      </div>
      <ProviderSectionFields
        options={options}
        optionsLoading={optionsLoading}
        section='delivery'
      />

      <Divider style={{ margin: '16px 0' }} />

      <CustomTitle
        text='Shipment'
        fontSize='18px'
        fontWeight='700'
        lineHeight='24px'
        marginBottom={16}
      />
      <div className={styles.fieldWrap}>
        <label className={styles.label}>Reference</label>
        <Form.Item name='specialty_reference' noStyle>
          <CustomInput placeholder='Reference' width='100%' height='36px' />
        </Form.Item>
      </div>
      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          Contents
          {contentsRequired ? (
            <span className={styles.required}> *</span>
          ) : null}
        </label>
        <Form.Item
          name='specialty_contents'
          rules={
            contentsRequired
              ? [{ required: true, message: 'Enter contents' }]
              : undefined
          }
          noStyle
        >
          <CustomInput placeholder='Contents' width='100%' height='36px' />
        </Form.Item>
      </div>
      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          Notify email
          {notifyEmailRequired ? (
            <span className={styles.required}> *</span>
          ) : null}
        </label>
        <Form.Item
          name='specialty_notify_email'
          rules={
            notifyEmailRequired
              ? [
                  { required: true, message: 'Enter a notification email' },
                  { type: 'email', message: 'Enter a valid email' },
                ]
              : [
                  {
                    validator: async (_, value) => {
                      if (value == null || String(value).trim() === '') return;
                      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                        String(value).trim()
                      );
                      if (!ok) throw new Error('Enter a valid email');
                    },
                  },
                ]
          }
          noStyle
        >
          <CustomInput
            placeholder='ops@example.com'
            width='100%'
            height='36px'
          />
        </Form.Item>
      </div>
      <ProviderSectionFields
        options={options}
        optionsLoading={optionsLoading}
        section='shipment'
      />

      {requiresPieces ? (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <CustomTitle
            text='Packages'
            fontSize='18px'
            fontWeight='700'
            lineHeight='24px'
            marginBottom={16}
          />
          <Form.List name='specialty_pieces'>
            {(fields) => (
              <>
                {fields.map((field) => (
                  <div key={field.key}>
                    {showPackageQuantity ? (
                      <div className={styles.fieldWrap}>
                        <label className={styles.label}>
                          Quantity <span className={styles.required}>*</span>
                        </label>
                        <Form.Item
                          name={[field.name, 'quantity']}
                          initialValue={1}
                          rules={[
                            { required: true, message: 'Enter quantity' },
                            {
                              validator: async (_, value) => {
                                const n = Number(value);
                                if (
                                  !Number.isFinite(n) ||
                                  !Number.isInteger(n) ||
                                  n < 1
                                ) {
                                  throw new Error(
                                    'Quantity must be a whole number of at least 1'
                                  );
                                }
                              },
                            },
                          ]}
                          noStyle
                        >
                          <CustomInput
                            placeholder='1'
                            width='100%'
                            height='36px'
                          />
                        </Form.Item>
                      </div>
                    ) : null}
                    <Row gutter={12}>
                      <Col span={8}>
                        <div className={styles.fieldWrap}>
                          <label className={styles.label}>
                            Weight <span className={styles.required}>*</span>
                          </label>
                          <Form.Item
                            name={[field.name, 'weight']}
                            rules={[
                              { required: true, message: 'Enter weight' },
                            ]}
                            noStyle
                          >
                            <CustomInput
                              placeholder='Weight'
                              width='100%'
                              height='36px'
                            />
                          </Form.Item>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className={styles.fieldWrap}>
                          <label className={styles.label}>Weight unit</label>
                          <Form.Item
                            name={[field.name, 'weightUnit']}
                            noStyle
                            initialValue='LB'
                          >
                            <CustomSelect
                              width='100%'
                              height='36px'
                              options={[
                                { label: 'LB', value: 'LB' },
                                { label: 'KG', value: 'KG' },
                              ]}
                            />
                          </Form.Item>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className={styles.fieldWrap}>
                          <label className={styles.label}>Dim unit</label>
                          <Form.Item
                            name={[field.name, 'dimensionUnit']}
                            noStyle
                            initialValue='IN'
                          >
                            <CustomSelect
                              width='100%'
                              height='36px'
                              options={[
                                { label: 'IN', value: 'IN' },
                                { label: 'CM', value: 'CM' },
                              ]}
                            />
                          </Form.Item>
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={12}>
                      {DIMENSION_FIELDS.map(({ key, label, placeholder }) => (
                        <Col span={8} key={key}>
                          <div className={styles.fieldWrap}>
                            <label className={styles.label}>
                              {label}
                              {dimensionsRequired ? (
                                <span className={styles.required}> *</span>
                              ) : null}
                            </label>
                            <Form.Item
                              name={[field.name, key]}
                              rules={
                                dimensionsRequired
                                  ? [
                                      {
                                        required: true,
                                        message: `Enter ${label.toLowerCase()}`,
                                      },
                                    ]
                                  : undefined
                              }
                              noStyle
                            >
                              <CustomInput
                                placeholder={placeholder}
                                width='100%'
                                height='36px'
                              />
                            </Form.Item>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}
              </>
            )}
          </Form.List>
          <ProviderSectionFields
            options={options}
            optionsLoading={optionsLoading}
            section='packages'
          />
        </>
      ) : (
        <ProviderSectionFields
          options={options}
          optionsLoading={optionsLoading}
          section='packages'
        />
      )}

      {(optionsLoading ||
        (Array.isArray(options?.fields) &&
          options.fields.some(
            (f) => (f.section || 'provider') === 'provider'
          ))) && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <ProviderSectionFields
            options={options}
            optionsLoading={optionsLoading}
            section='provider'
            title='Provider details'
          />
        </>
      )}
      {zoomlogiFields}
      <Divider style={{ margin: '24px 0 16px' }} />
    </>
  );
};

export default SpecialtyBookingFields;
